import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './services/auth';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import Login from './components/Auth/Login';
import Layout from './components/Layout/Layout';
 
import Dashboard from './components/Dashboard/Dashboard';
import ScenariosList from './components/Scenarios/ScenariosList';
import CreateScenarioWizard from './components/Scenarios/CreateScenario/CreateScenarioWizard';
import FacilitiesList from './components/Facilities/FacilitiesList';
import AggregateDashboard from './components/Aggregate/AggregateDashboard';
import HotspotEmissions from './components/Hotspot/HotspotEmissions';
import VoiceAssistant from './components/VoiceAssistant';
import UserManual from './components/UserManual/UserManual';

// CRITICAL: Import CSS in correct order - design-tokens MUST come first!
import './styles/design-tokens.css';      // 1. Foundation - Design tokens
import './styles/globals.css';            // 2. Global styles and utilities
import './styles/components.css';         // 3. Component library
import './styles/sidebar.css';            // 4. Sidebar styles
import './styles/dashboard.css';          // 6. Dashboard-specific styles
 
 
const logger = {
  log: (...args) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('[App]', ...args);
    }
  },
  error: (...args) => {
    console.error('[App ERROR]', ...args);
  },
  warn: (...args) => {
    console.warn('[App WARN]', ...args);
  }
};
 
// Login route wrapper to prevent authenticated users from seeing login
function LoginRoute() {
  const { isAuthenticated, loading } = useAuth();
 
  if (loading) {
    return (
      <div className="loading-container" style={{ minHeight: '100vh' }}>
        <div className="loading-spinner loading-spinner-lg"></div>
        <span className="loading-text">Loading...</span>
      </div>
    );
  }
 
  if (isAuthenticated) {
    console.log('[LoginRoute] Already authenticated, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  }
 
  return <Login />;
}
 
function AppContent() {
  const auth = useAuth();
  const { isAuthenticated } = auth;
  const [voiceAssistantEnabled, setVoiceAssistantEnabled] = useState(false);
  const [voiceAssistantVisible, setVoiceAssistantVisible] = useState(false);
 
  useEffect(() => {
    // Check if voice assistant is available on backend
    if (isAuthenticated) {
      const checkBackend = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/health`);
          const data = await response.json();
          setVoiceAssistantEnabled(data.features?.voice_enabled === true);
          logger.log('🎤 Voice Assistant backend status:', data.features?.voice_enabled ? 'Available' : 'Disabled');
        } catch (error) {
          logger.warn('⚠️ Could not check Voice Assistant backend status:', error);
          setVoiceAssistantEnabled(false);
        }
      };
 
      checkBackend();
      const timeoutId = setTimeout(checkBackend, 1000);
     
      return () => clearTimeout(timeoutId);
    } else {
      setVoiceAssistantEnabled(false);
    }
  }, [isAuthenticated]);
 
  return (
    <div className="App">
      <Routes>
        {/* Public route with redirect protection */}
        <Route path="/login" element={<LoginRoute />} />
       
        {/* Protected routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <Layout>
              <Navigate to="/dashboard" replace />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <Layout>
              <Dashboard />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/scenarios" element={
          <ProtectedRoute>
            <Layout>
              <ScenariosList />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/scenarios/create" element={
          <ProtectedRoute>
            <Layout>
              <CreateScenarioWizard />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/aggregate" element={
          <ProtectedRoute>
            <Layout>
              <AggregateDashboard />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/hotspot" element={
          <ProtectedRoute>
            <Layout>
              <HotspotEmissions />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/facilities" element={
          <ProtectedRoute>
            <Layout>
              <FacilitiesList />
            </Layout>
          </ProtectedRoute>
        } />
        <Route path="/user-manual" element={
          <ProtectedRoute>
            <Layout>
              <UserManual />
            </Layout>
          </ProtectedRoute>
        } />
      </Routes>
     
      {/* Voice Assistant */}
      {isAuthenticated && voiceAssistantEnabled && voiceAssistantVisible && (
        <VoiceAssistant onClose={() => setVoiceAssistantVisible(false)} />
      )}
     
      {isAuthenticated && voiceAssistantEnabled && !voiceAssistantVisible && (
        <button
          className="voice-assistant-toggle-btn"
          onClick={() => setVoiceAssistantVisible(true)}
          title="Open LCA Assistant"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        </button>
      )}
    </div>
  );
}
 
function App() {
  return (
    <AuthProvider>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <AppContent />
      </Router>
    </AuthProvider>
  );
}
 
export default App;